'use client'

import { type ComponentProps, type ReactNode } from 'react'

import { ParsedTemplate } from '@/components/common/ParsedTemplate'
import { SectionLabel } from '@/components/common/SectionLabel'
import { StyledContent } from '@/components/common/StyledContent'
import { usePage } from '@/components/providers/PageProvider'
import { cn } from '@/utils/commonUtil'

import { FeaturedCommonArticles } from '../FeaturedCommon/FeaturedCommonArticles/FeaturedCommonArticles'
import { FeaturedCommonFilters } from '../FeaturedCommon/FeaturedCommonFilters/FeaturedCommonFilters'
import { FeaturedCommonRoot } from '../FeaturedCommon/FeaturedCommonRoot/FeaturedCommonRoot'

import styles from './FeaturedStandard.module.scss'

type Props = {
  children?: ReactNode
  hideSubtitle?: boolean
  label?: string
  layout?: 'default' | 'fullWidth' | 'centered'
  showFilters?: boolean
  styled?: boolean
  subtitle?: string
  title?: string
} & ComponentProps<typeof FeaturedCommonRoot>

export const FeaturedStandard = ({
  children,
  hideSubtitle,
  label: labelProp,
  layout = 'default',
  showFilters,
  styled,
  subtitle: subtitleProp,
  title: titleProp,
  ...props
}: Props) => {
  const { page } = usePage()

  const title = titleProp || page?.metadata?.title
  const subtitle = subtitleProp || page?.metadata?.description
  const label = labelProp || page?.metadata?.label
  const articlePreviews = page?.articlePreviews

  const content = styled ? <StyledContent>{children}</StyledContent> : children

  const splashProps: ComponentProps<typeof FeaturedCommonRoot>['splashProps'] =
    layout === 'centered' ? { subtle: true } : undefined

  return (
    <FeaturedCommonRoot {...props} splashProps={splashProps}>
      <div className={cn(styles.container, { layout, styles })}>
        <div className={styles.info}>
          {label && (
            <div className={styles.label}>
              <SectionLabel label={label} />
            </div>
          )}
          {title && (
            <h1 className={styles.title}>
              <ParsedTemplate>{title}</ParsedTemplate>
            </h1>
          )}
          {!hideSubtitle && subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          {showFilters && <FeaturedCommonFilters />}
          {children && <div className={styles.content}>{content}</div>}
        </div>
        {articlePreviews && (
          <div className={styles.articles}>
            <FeaturedCommonArticles />
          </div>
        )}
      </div>
    </FeaturedCommonRoot>
  )
}
